import { getMomentTz, MESApiService } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	GasConsumptionData,
	GasConsumptionReportRequest,
	GasConsumptionReportSummary,
	GasConsumptionReportZoneRow,
	GasConsumptionMainBucket,
	GasConsumptionMillRolling,
	GasConsumptionMiniBucket,
	GasConsumptionRequest,
	GasConsumptionSummary,
	GasConsumptionZone,
} from "../models/GasConsumption";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	UTILITY_CONSUMPTION_REPORT_ROW_KIND,
	type UtilityConsumptionReportData,
	type UtilityConsumptionReportRow,
} from "../models/UtilityConsumption";

const moment = getMomentTz();

const toZone = (row: GasConsumptionZone): GasConsumptionZone => ({
	...row,
	LatestReadTimeUtc: row.LatestReadTimeUtc
		? moment.utc(row.LatestReadTimeUtc).toDate()
		: null,
});

export const getGasConsumptionZones = async (
	targetId: number | null,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[SWV].[GetGasConsumptionZones]",
		[{ name: "TargetId", value: targetId }],
		signal,
	);

	if (resp.ok) {
		return (get(resp, "data.tables.0.rows", []) as GasConsumptionZone[]).map(
			toZone,
		);
	}

	throw new Error(resp.message || "Error fetching gas zones");
};

export const getGasConsumption = async (
	request: GasConsumptionRequest,
	signal?: AbortSignal,
): Promise<GasConsumptionData> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[SWV].[GetGasConsumption]",
		[
			{ name: "TargetId", value: request.targetId },
			{
				name: "ZoneKeysJson",
				value:
					request.zoneKeys === null ? null : JSON.stringify(request.zoneKeys),
			},
			{ name: "Mode", value: request.mode },
			{ name: "StartUtc", value: request.startUtc.toISOString() },
			{ name: "EndUtc", value: request.endUtc.toISOString() },
			{ name: "WaitForEveryZone", value: request.waitForEveryZone ?? null },
		],
		signal,
	);

	if (resp.ok) {
		const tables = get(resp, "data.tables", {});
		const summary = get(tables, "1.rows.0", {}) as GasConsumptionSummary;

		return {
			chartZones: (get(tables, "0.rows", []) as GasConsumptionZone[]).map(
				toZone,
			),
			summary: {
				...summary,
				DataThroughUtc: summary.DataThroughUtc
					? moment.utc(summary.DataThroughUtc).toDate()
					: null,
				DataSinceUtc: summary.DataSinceUtc
					? moment.utc(summary.DataSinceUtc).toDate()
					: null,
				PreviousRollingStartUtc: summary.PreviousRollingStartUtc
					? moment.utc(summary.PreviousRollingStartUtc).toDate()
					: null,
				PreviousRollingEndUtc: summary.PreviousRollingEndUtc
					? moment.utc(summary.PreviousRollingEndUtc).toDate()
					: null,
				MainCostBillStart: summary.MainCostBillStart
					? moment.utc(summary.MainCostBillStart).toDate()
					: null,
				MainCostBillEnd: summary.MainCostBillEnd
					? moment.utc(summary.MainCostBillEnd).toDate()
					: null,
				CurrentRollingCostBillStart: summary.CurrentRollingCostBillStart
					? moment.utc(summary.CurrentRollingCostBillStart).toDate()
					: null,
				CurrentRollingCostBillEnd: summary.CurrentRollingCostBillEnd
					? moment.utc(summary.CurrentRollingCostBillEnd).toDate()
					: null,
				IsMainCostApproximate: !!summary.IsMainCostApproximate,
				IsCurrentRollingCostApproximate:
					!!summary.IsCurrentRollingCostApproximate,
				IsPreviousRollingCostApproximate:
					!!summary.IsPreviousRollingCostApproximate,
			},
			miniBuckets: (
				get(tables, "2.rows", []) as GasConsumptionMiniBucket[]
			).map((row) => ({
				...row,
				CurrentBucketStartUtc: row.CurrentBucketStartUtc
					? moment.utc(row.CurrentBucketStartUtc).toDate()
					: null,
				PreviousBucketStartUtc: moment.utc(row.PreviousBucketStartUtc).toDate(),
			})),
			mainBuckets: (
				get(tables, "3.rows", []) as GasConsumptionMainBucket[]
			).map((row) => ({
				...row,
				BucketStartUtc: moment.utc(row.BucketStartUtc).toDate(),
			})),
			millRolling: (
				get(tables, "4.rows", []) as GasConsumptionMillRolling[]
			).map((row) => ({
				...row,
				CurrentRollingStartUtc: row.CurrentRollingStartUtc
					? moment.utc(row.CurrentRollingStartUtc).toDate()
					: null,
			})),
		};
	}

	throw new Error(resp.message || "Error fetching gas consumption");
};

export const getGasConsumptionReport = async (
	request: GasConsumptionReportRequest,
	signal?: AbortSignal,
): Promise<UtilityConsumptionReportData> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[SWV].[GetGasConsumptionReport]",
		[
			{ name: "TargetId", value: request.targetId },
			{
				name: "ZoneKeysJson",
				value:
					request.zoneKeys === null ? null : JSON.stringify(request.zoneKeys),
			},
			{ name: "Mode", value: request.mode },
			...(request.mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION
				? [{ name: "SectionId", value: request.sectionId }]
				: []),
			{ name: "StartUtc", value: request.startUtc.toISOString() },
			{ name: "EndUtc", value: request.endUtc.toISOString() },
		],
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching gas consumption report");
	}

	const rows = get(
		resp,
		"data.tables.0.rows",
		[],
	) as GasConsumptionReportZoneRow[];
	const summary = get(
		resp,
		"data.tables.1.rows.0",
		{},
	) as GasConsumptionReportSummary;

	const isSection = request.mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION;
	const meterIds: string[] = [];
	const seenZoneKeys = new Set<string>();
	const meterTotalValue: Record<string, number | null> = {};
	const meterTotalPartial: Record<string, boolean> = {};
	const reportRows: UtilityConsumptionReportRow[] = [];
	const reportRowsByKey = new Map<string, UtilityConsumptionReportRow>();

	for (const row of rows) {
		if (!seenZoneKeys.has(row.ZoneKey)) {
			seenZoneKeys.add(row.ZoneKey);
			meterIds.push(row.ZoneKey);
		}
		meterTotalValue[row.ZoneKey] = row.ZoneTotalMmbtu;
		meterTotalPartial[row.ZoneKey] = !!row.IsZoneTotalMmbtuPartial;

		const rowKey = isSection
			? `${row.MillAssetId}:${row.EventId}`
			: row.PeriodStartUtc;

		let reportRow = reportRowsByKey.get(rowKey);
		if (reportRow === undefined) {
			const figures = {
				MeterValue: {},
				MeterCoverage: {},
				MeterPartial: {},
				PeriodValue: row.PeriodMmbtu,
				ValuePerTon: row.MmbtuPerTon,
				ValuePerBillet: row.MmbtuPerBillet,
				PeriodCost: row.PeriodCost,
				CostPerTon: row.CostPerTon,
				IsCostApproximate: !!row.IsCostApproximate,
				IsValuePartial: !!row.IsMmbtuPartial,
				IsValueApproximate: !!row.IsMmbtuApproximate,
				IsCostPartial: !!row.IsCostPartial,
			};

			reportRow = isSection
				? {
						...figures,
						Kind: UTILITY_CONSUMPTION_REPORT_ROW_KIND.SECTION,
						MillAssetId: row.MillAssetId as number,
						MillName: row.MillName as string,
						EventId: row.EventId as number,
						SectionId: row.SectionId as number,
						SectionName: row.SectionName as string,
						EventStartUtc: moment.utc(row.EventStartUtc).toDate(),
						EventEndUtc: row.EventEndUtc
							? moment.utc(row.EventEndUtc).toDate()
							: null,
						PeriodStartUtc: moment.utc(row.PeriodStartUtc).toDate(),
						PeriodEndUtc: moment.utc(row.PeriodEndUtc).toDate(),
					}
				: {
						...figures,
						Kind: UTILITY_CONSUMPTION_REPORT_ROW_KIND.CALENDAR,
						PeriodStartUtc: moment.utc(row.PeriodStartUtc).toDate(),
					};

			reportRowsByKey.set(rowKey, reportRow);
			reportRows.push(reportRow);
		}

		reportRow.MeterValue[row.ZoneKey] = row.ZoneMmbtu;
		reportRow.MeterPartial[row.ZoneKey] = !!row.IsZoneMmbtuPartial;
		reportRow.MeterCoverage[row.ZoneKey] =
			row.ZoneReadings === null ||
			row.ZoneExpectedReadings === null ||
			row.ZoneExpectedReadings === 0
				? null
				: (row.ZoneReadings / row.ZoneExpectedReadings) * 100;
	}

	return {
		MeterIds: meterIds,
		Rows: reportRows,
		MeterTotalValue: meterTotalValue,
		MeterTotalPartial: meterTotalPartial,
		GrandTotalValue: summary.GrandTotalMmbtu,
		GrandValuePerTon: summary.GrandMmbtuPerTon,
		GrandValuePerBillet: summary.GrandMmbtuPerBillet,
		GrandTotalCost: summary.GrandTotalCost ?? null,
		GrandCostPerTon: summary.GrandCostPerTon ?? null,
		IsCostApproximate: !!summary.IsCostApproximate,
		IsValuePartial: !!summary.IsMmbtuPartial,
		IsValueApproximate: !!summary.IsMmbtuApproximate,
		IsCostPartial: !!summary.IsCostPartial,
	};
};
